import { makeAutoObservable, runInAction } from "mobx";
import { IMasterRating, IPublicMaster, IPublicMasterPost, IPublicMasterReview, IPublicMasterService, PublicMasterProfileService } from "./PublicMasterProfileService";

export class PublicMasterProfileStore {
  public Master: IPublicMaster | null = null;
  public Rating: IMasterRating | null = null;

  public IsLoading = false;
  public Error = "";

  public Posts: IPublicMasterPost[] = [];
  public NextCursor: number | null = null;
  public HasMorePosts = false;
  public IsLoadingPosts = false;
  public PostsPerPageLimit = 10;

  public Services: IPublicMasterService[] = [];

  public Reviews: IPublicMasterReview[] = [];
  public ReviewsNextCursor: number | null = null;
  public HasMoreReviews = false;
  public IsLoadingReviews = false;
  public ReviewsPerPageLimit = 10;
  public SelectedReviewServiceId: number | null = null;

  public ActiveTab: "works" | "services" | "reviews" = "works";

  constructor() {
    makeAutoObservable(this);
  }

  public setActiveTab(tab: "works" | "services" | "reviews") {
    this.ActiveTab = tab;

    if (tab === "reviews" && !this.Reviews.length && !this.IsLoadingReviews) {
      this.loadReviews(true);
    }
  }

  public async load(id: number) {
    this.IsLoading = true;
    this.Error = "";

    try {
      const [master, rating, postsResponse, services] = await Promise.all([
        PublicMasterProfileService.GetMaster(id),
        PublicMasterProfileService.GetRating(id),
        PublicMasterProfileService.GetMasterPosts(id, this.PostsPerPageLimit),
        PublicMasterProfileService.GetMasterServices(id),
      ]);

      runInAction(() => {
        this.Master = master;
        this.Rating = rating;
        this.Posts = postsResponse.items ?? [];
        this.NextCursor = postsResponse.nextCursor;
        this.HasMorePosts = postsResponse.hasMore;
        this.Services = services ?? [];
      });
    } catch {
      runInAction(() => {
        this.Error = "Не удалось загрузить профиль мастера";
      });
    } finally {
      runInAction(() => {
        this.IsLoading = false;
      });
    }
  }

  public async loadMorePosts() {
    if (!this.Master || !this.HasMorePosts || this.IsLoadingPosts) return;

    this.IsLoadingPosts = true;

    try {
      const response = await PublicMasterProfileService.GetMasterPosts(this.Master.id, this.PostsPerPageLimit, this.NextCursor);

      runInAction(() => {
        this.Posts = [...this.Posts, ...(response.items ?? [])];
        this.NextCursor = response.nextCursor;
        this.HasMorePosts = response.hasMore;
      });
    } catch {
      runInAction(() => {
        this.Error = "Не удалось загрузить посты";
      });
    } finally {
      runInAction(() => {
        this.IsLoadingPosts = false;
      });
    }
  }

  public setReviewService(serviceId: number | null) {
    if (this.SelectedReviewServiceId === serviceId) return;

    this.SelectedReviewServiceId = serviceId;
    this.loadReviews(true);
  }

  public async loadReviews(reset = false) {
    if (!this.Master) return;
    if (this.IsLoadingReviews) return;
    if (!reset && !this.HasMoreReviews) return;

    this.IsLoadingReviews = true;

    try {
      const cursor = reset ? null : this.ReviewsNextCursor;

      const response = this.SelectedReviewServiceId
        ? await PublicMasterProfileService.GetServiceReviews(this.SelectedReviewServiceId, this.ReviewsPerPageLimit, cursor)
        : await PublicMasterProfileService.GetMasterReviews(this.Master.id, this.ReviewsPerPageLimit, cursor);

      runInAction(() => {
        this.Reviews = reset ? response.reviews ?? [] : [...this.Reviews, ...(response.reviews ?? [])];
        this.ReviewsNextCursor = response.nextCursor;
        this.HasMoreReviews = response.hasMore;
      });
    } catch {
      runInAction(() => {
        this.Error = "Не удалось загрузить отзывы";
      });
    } finally {
      runInAction(() => {
        this.IsLoadingReviews = false;
      });
    }
  }

  public getServiceName(serviceId?: number) {
    if (!serviceId) return "";

    return this.Services.find((service) => service.id === serviceId)?.name ?? "";
  }

  public getReviewAuthor(review: IPublicMasterReview) {
    if (review.authorName?.trim()) {
      return review.authorName;
    }

    if (review.userId) {
      return `Пользователь${review.userId}`;
    }

    return "Пользователь";
  }
}
